import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './AppointmentCalendar.css';
import Header from '../../components/Header';
import { authService } from '../../services';
import Footer from '../../components/Footer';

export default function AppointmentCalendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    timeZone: 'Asia/Ho_Chi_Minh' // Đảm bảo timezone nhất quán
  }));
  const [viewMode, setViewMode] = useState('Day');
  const [currentWeek, setCurrentWeek] = useState(0);
  const [activeSection, setActiveSection] = useState('appointment');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Which month the date picker is currently showing (so prev/next works)
  const [displayMonthDate, setDisplayMonthDate] = useState(() => {
    try { return parseDateString(selectedDate); } catch(e) { return new Date(); }
  });
  const [userFormData, setUserFormData] = useState({
    fullName: '',
    nickName: '',
    email: '',
    gender: 'male',
    country: 'Vietnam',
    language: 'vi',
    phone: ''
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [expertInfo, setExpertInfo] = useState(null);
  const [selectedExpertId, setSelectedExpertId] = useState(null);
  const [expertsList, setExpertsList] = useState([]);
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalAppointment, setModalAppointment] = useState(null);
  const [modalClientName, setModalClientName] = useState(null);
  const [modalClientLoading, setModalClientLoading] = useState(false);
  const modalClientCacheRef = React.useRef({});
  const [userProfile, setUserProfile] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Load current user and set form data
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      console.log('AppointmentCalendar: Current user loaded:', user);
      
      // Set form data from user info
      setUserFormData({
        fullName: user.name || user.username || '',
        nickName: user.nickName || '',
        email: user.email || '',
        gender: user.gender || 'male',
        country: user.country || 'Vietnam',
        language: user.language || 'vi',
        phone: user.phone || ''
      });
    } else {
      console.log('AppointmentCalendar: No user found, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  // Handle URL parameters to set active section
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const section = urlParams.get('section');
    const edit = urlParams.get('edit');

    if (section && ['user-details', 'profile', 'psychological-profile', 'mental-health', 'therapy-goals', 'appointment', 'session-notes'].includes(section)) {
      setActiveSection(section);
    }

    if (edit === 'true') {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }

    // Get expert info from localStorage or location state
    const savedExpertInfo = localStorage.getItem('selectedExpert');
    if (savedExpertInfo) {
      const parsed = JSON.parse(savedExpertInfo);
      setExpertInfo(parsed);
      setSelectedExpertId(parsed.id || parsed.Id || null);
    } else if (location.state?.expert) {
      setExpertInfo(location.state.expert);
      setSelectedExpertId(location.state.expert.id || location.state.expert.Id || null);
    }
    // Try to load an experts list from localStorage or a service
    const savedExperts = localStorage.getItem('expertsList');
    if (savedExperts) {
      try { setExpertsList(JSON.parse(savedExperts)); } catch(e) { /* ignore */ }
    }
  }, [location.search, location.state]);

  // Load user profile when in user-details section
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('section') === 'user-details') {
      const load = async () => {
        try {
          const { userProfileService } = await import('../../services');
          if (currentUser && currentUser.id) {
            const res = await userProfileService.getByUserId(currentUser.id);
            setUserProfile(res || null);
            // If profile contains fields we want to show in the form, apply them
            if (res) {
              setUserFormData(prev => ({
                ...prev,
                fullName: res.fullName || prev.fullName,
                email: res.email || prev.email,
                phone: res.phone || prev.phone,
                country: res.country || prev.country,
                language: res.language || prev.language
              }));
            }
          }
        } catch (err) {
          console.error('Failed to load user profile', err);
        }
      };
      load();
    }
  }, [location.search, currentUser]);

  // Load therapists list once so the doctor selector can be populated
  useEffect(() => {
    let mounted = true;
    const loadTherapists = async () => {
      try {
        const svc = (await import('../../services/therapistService')).therapistService;
        if (svc && svc.getAll) {
          const res = await svc.getAll();
          if (!mounted) return;
          const list = Array.isArray(res) ? res : (res.data || []);
          setExpertsList(list.map(n => ({ id: n.id || n.Id, name: n.name || n.fullName || n.displayName })));
          // default to first expert as the user requested
          if (list.length > 0 && !selectedExpertId) {
            const firstId = list[0].id || list[0].Id;
            setSelectedExpertId(firstId);
            setExpertInfo(list[0]);
            localStorage.setItem('selectedExpert', JSON.stringify(list[0]));
          }
        }
      } catch (err) {
        console.error('Failed to load therapists for selector', err);
      }
    };
    loadTherapists();
    return () => { mounted = false };
  }, [selectedExpertId]);

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', 
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
  ];

  // Generate week days
  const getWeekDays = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    // move to start of week (Sunday)
    startOfWeek.setDate(today.getDate() - today.getDay() + currentWeek * 7);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push({
        date: date.getDate(),
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullIso: date.toISOString().split('T')[0]
      });
    }
    return weekDays;
  };

  const weekDays = getWeekDays();

  // Handle navigation to booking session
  const handleNewAppointment = () => {
    // Navigate to the booking session page
    navigate('/book-expert');
  };

  // Handle Plus button click (could be for subscription)
  const handlePlusClick = () => {
    navigate('/subscription');
  };

  // Handle edit button click
  const handleEditProfile = () => {
    setActiveSection('user-details');
    setIsEditMode(true);
  };

  // Handle retake assessment
  const handleRetakeAssessment = () => {
    navigate('/questionnaire');
  };

  // Handle download profile
  const handleDownloadProfile = () => {
    alert('Downloading profile...');
  };

  // Handle share with therapist
  const handleShareProfile = () => {
    alert('Sharing profile with therapist...');
  };

  // Handle navigation to different sections
  const handleNavigation = (section) => {
    setActiveSection(section);
    // Update URL parameters to reflect current section
    const url = new URL(window.location);
    url.searchParams.set('section', section);
    window.history.pushState({}, '', url);
  };

  // Handle appointment click - open details modal and fetch appointment
  const handleAppointmentClick = (appointment) => {
    console.log('Appointment clicked:', appointment);
    const openModal = async () => {
      try {
        setIsModalOpen(true);
        setModalLoading(true);
        setModalError(null);
        
        // Sử dụng dữ liệu appointment đã có trước, sau đó fetch từ server để cập nhật
        setModalAppointment(appointment);
        
        const svc = (await import('../../services/appointmentService')).appointmentService;
        const appt = await svc.getAppointmentById(appointment.id);
        console.log('Fetched appointment from server:', appt);
        setModalAppointment(appt);
  // reset previously resolved client name
  setModalClientName(null);
        // try to resolve client name if payload only has an id
        try {
          setModalClientLoading(true);
          const clientIdRaw = appt.clientId || appt.client || (appt.client && typeof appt.client === 'string' ? appt.client : null);
          const clientId = clientIdRaw ? String(clientIdRaw) : null;
          
          if (clientId) {
            console.log('Loading client info for ID:', clientId);
            
            // Check if this ID belongs to the current user (client) or is an expert ID
            if (currentUser && clientId === currentUser.id) {
              // This is the current user's appointment
              console.log('This is current user appointment');
              setModalClientName(currentUser.username || currentUser.name || 'You');
            } else {
              // This might be an expert ID or another client ID
              console.log('This appears to be an expert or other client appointment');
              setModalClientName(`Expert/Client ${clientId.substring(0, 8)}...`);
            }
          } else {
            console.log('No client ID found, using default name');
            setModalClientName('Unknown Client');
          }
        } catch (e) {
          console.log('Using default client name due to error');
          setModalClientName('Unknown Client');
        } finally {
          setModalClientLoading(false);
        }
      } catch (err) {
        console.error('Failed to load appointment details', err);
        setModalError(err?.response?.data || err.message || String(err));
      } finally {
        setModalLoading(false);
      }
    };
    openModal();
  };

  const handleGoToClientProfile = async (expertId) => {
    if (!expertId) return;
    
    try {
      // Try to get fresh expert data from API
      const { therapistService } = await import('../../services');
      const freshExpertData = await therapistService.getById(expertId);
      
      if (freshExpertData) {
        // Map API response to our expected format
        const mappedExpert = {
          id: freshExpertData.id || freshExpertData.Id,
          name: freshExpertData.name || freshExpertData.fullName || freshExpertData.displayName || 'Dr. Expert',
          specialty: freshExpertData.specialty || freshExpertData.specialization || 'Tâm lý học',
          experience: freshExpertData.experience || freshExpertData.yearsOfExperience || '5 năm',
          rating: freshExpertData.rating || freshExpertData.averageRating || 4.5,
          price: freshExpertData.price || freshExpertData.hourlyRate || '2.000.000đ/buổi',
          image: freshExpertData.avatarUrl || freshExpertData.avatar_url || freshExpertData.image || freshExpertData.avatar || freshExpertData.profilePicture || 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=400&fit=crop&crop=face',
          description: freshExpertData.description || freshExpertData.bio || freshExpertData.about || 'Chuyên gia tâm lý với nhiều năm kinh nghiệm trong việc hỗ trợ khách hàng.',
          availability: freshExpertData.availability || (freshExpertData.isAvailable ? 'Có lịch' : 'Không có lịch'),
          location: freshExpertData.location || freshExpertData.city || 'Ho Chi Minh City',
          phone: freshExpertData.phone || freshExpertData.phoneNumber || '+84 123 456 789',
          email: freshExpertData.email || freshExpertData.emailAddress || 'expert@example.com',
          education: freshExpertData.education || freshExpertData.qualifications || 'PhD in Psychology',
          certifications: freshExpertData.certifications || freshExpertData.credentials || ['Psychology License'],
          languages: freshExpertData.languages || freshExpertData.spokenLanguages || ['Vietnamese', 'English'],
          joinDate: freshExpertData.joinDate || freshExpertData.createdAt || '2020',
          fullName: freshExpertData.fullName || freshExpertData.name || 'Dr. Expert',
          displayName: freshExpertData.displayName || freshExpertData.name || 'Dr. Expert'
        };
        
        localStorage.setItem('selectedExpertInfo', JSON.stringify(mappedExpert));
        console.log('Saved fresh expert info to localStorage:', mappedExpert);
      } else {
        // Fallback to current expert info
        if (expertInfo) {
          localStorage.setItem('selectedExpertInfo', JSON.stringify(expertInfo));
          console.log('Saved current expert info to localStorage:', expertInfo);
        }
      }
      
      // Save current appointment data for Profile page
      if (modalAppointment) {
        localStorage.setItem('currentAppointment', JSON.stringify(modalAppointment));
        console.log('Saved appointment data to localStorage:', modalAppointment);
      }
      
    } catch (error) {
      console.error('Failed to get fresh expert data:', error);
      // Fallback to current expert info
      if (expertInfo) {
        localStorage.setItem('selectedExpertInfo', JSON.stringify(expertInfo));
        console.log('Saved current expert info to localStorage (fallback):', expertInfo);
      }
    }
    
    // navigate to expert profile page (since this is an expert's calendar view)
    navigate(`/profile/${expertId}`);
    setIsModalOpen(false);
  };

  const handleEditAppointment = async (appt) => {
    // Prefer in-place edit route if available, otherwise navigate to an edit page
    try {
      setModalLoading(true);
      const svc = (await import('../../services/appointmentService')).appointmentService;
      if (svc && svc.updateAppointment) {
        // open an edit UI - for now navigate to edit page
        setIsModalOpen(false);
        navigate(`/appointments/edit/${appt.id}`, { state: { appointment: appt } });
      } else {
        setIsModalOpen(false);
        navigate(`/appointments/edit/${appt.id}`, { state: { appointment: appt } });
      }
    } catch (err) {
      console.error('Failed to start edit flow', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCancelAppointment = async (appt) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      setModalLoading(true);
      const svc = (await import('../../services/appointmentService')).appointmentService;
      if (svc && svc.cancelAppointment) {
        await svc.cancelAppointment(appt.id);
      } else if (svc && svc.deleteAppointment) {
        await svc.deleteAppointment(appt.id);
      } else {
        // fallback: try PATCH status to Cancelled
        if (svc && svc.updateAppointment) {
          await svc.updateAppointment(appt.id, { status: 'Cancelled' });
        }
      }
      // refresh appointments
      setReloadToken(t => t + 1);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to cancel appointment', err);
      setModalError(err?.response?.data || err.message || String(err));
    } finally {
      setModalLoading(false);
    }
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = () => {
    console.log('Mobile menu toggle clicked! Current state:', isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
    console.log('New state will be:', !isMobileMenuOpen);
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setUserFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save user details
  const handleSaveUserDetails = () => {
    // If we are editing the user-details section, persist to the backend via UserProfiles API
    const q = new URLSearchParams(location.search);
    if (q.get('section') === 'user-details' && currentUser && currentUser.id) {
      (async () => {
        try {
          setIsSavingProfile(true);
          const { userProfileService } = await import('../../services');
          // Build payload that matches the backend UserProfile shape
          const profilePayload = {
            userId: currentUser.id,
            avatarUrl: userProfile?.avatarUrl || null,
            anonymousDisplayName: userFormData.nickName || userProfile?.anonymousDisplayName || null,
            visibilityPreferenceValue: userProfile?.visibilityPreferenceValue || 0,
            bio: userProfile?.bio || null,
            // dateOfBirth on backend is DateOnly; send null unless you have a proper value
            dateOfBirth: userProfile?.dateOfBirth || null,
            gender: userFormData.gender || userProfile?.gender || null,
            psychometricAnswers: userProfile?.psychometricAnswers || null
          };

          console.log('Saving user profile payload:', profilePayload);
          if (userProfile && userProfile.id) {
            // For PUT the controller expects the profile.Id to match the route id
            profilePayload.id = userProfile.id;
            const res = await userProfileService.update(userProfile.id, profilePayload);
            console.log('Update response:', res);
          } else {
            // POST will let the server create an Id if missing
            const res = await userProfileService.create(profilePayload);
            console.log('Create response:', res);
          }
          alert('Profile updated successfully!');
          setIsEditMode(false);
          setActiveSection('user-details');
        } catch (err) {
          // Improve error visibility: show server validation message if present
          console.error('Failed to save profile', err?.response?.status, err?.response?.data || err?.message || err);
          alert('Failed to save profile: ' + (err?.response?.data?.title || err?.response?.data || err?.message || 'Unknown error'));
        } finally {
          setIsSavingProfile(false);
        }
      })();
    } else {
      // Save to localStorage as before for non-profile flows
      localStorage.setItem('userInfo', JSON.stringify({
        name: userFormData.fullName,
        email: userFormData.email, // Use form email
        phone: userFormData.phone,
        location: userFormData.country,
        gender: userFormData.gender,
        language: userFormData.language,
        nickName: userFormData.nickName
      }));
      alert('Profile updated successfully!');
      setIsEditMode(false);
      setActiveSection('user-details');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setActiveSection('user-details');
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    }));
    setShowDatePicker(false);
    setViewMode('Day');
  };

  // Parse date string to Date object
  const parseDateString = (dateString) => {
    const parts = dateString.split(' ');
    const day = parseInt(parts[0]);
    const month = parts[1];
    const year = parseInt(parts[2]);
    
    const monthMap = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    
    return new Date(year, monthMap[month], day);
  };

  // Generate calendar days for the provided month (starts on Sunday)
  const generateCalendarDays = (baseDate) => {
    const base = baseDate ? new Date(baseDate) : new Date();
    const month = base.getMonth();
    const year = base.getFullYear();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];
    // empty cells for days before start of month (Sunday=0)
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  };

  // keep the display month in sync when user picks a date elsewhere
  useEffect(() => {
    try { setDisplayMonthDate(parseDateString(selectedDate)); } catch(e) { /* ignore */ }
  }, [selectedDate]);

  // appointmentsData will hold mapped server appointments for rendering
  const appointments = appointmentsData;

  const currentTime = '08:45'; // Current time indicator

  // Helper: map server appointment object into UI appointment object
  const mapServerToUI = (srv) => {
    // Example server fields: scheduledFor, durationMinutes, client (User), statusValue
    const scheduledForStr = srv.scheduledFor || srv.ScheduledFor || srv.scheduled_for;
    console.log('Debug - Raw scheduledFor from server:', scheduledForStr);
    
    // Parse the ISO string - server trả về local time (GMT+7)
    const dt = new Date(scheduledForStr);
    console.log('Debug - Parsed date object (local time):', dt);
    console.log('Debug - Local date string:', dt.toLocaleDateString());
    console.log('Debug - Local time string:', dt.toLocaleTimeString());
    
    // Server đã gửi local time, không cần convert thêm
    const localDt = dt;
    console.log('Debug - Using local time directly:', localDt);
    
    // Extract time for calendar display
    const hh = String(localDt.getHours()).padStart(2, '0');
    const mm = String(localDt.getMinutes()).padStart(2, '0');
    const timeStr = `${hh}:${mm}`;
    console.log('Debug - Time string for calendar:', timeStr);
    console.log('Debug - Available time slots:', timeSlots);
    console.log('Debug - Time slot index:', timeSlots.indexOf(timeStr));
    
    const title = (srv.client && (srv.client.fullName || srv.client.name)) || srv.clientName || srv.clientId || 'Someone';
    const duration = srv.durationMinutes || srv.DurationMinutes || srv.duration || 30;
    const status = srv.status || srv.statusValue || srv.StatusValue || 'Scheduled';
    // map status to a color class
    const color = (status === 0 || String(status).toLowerCase().includes('scheduled')) ? 'yellow' : (String(status).toLowerCase().includes('confirm') || status === 1 ? 'orange' : 'teal');
    // include dateIso to allow grouping by day (yyyy-mm-dd) - use local date
    const dateIso = localDt.toLocaleDateString('en-CA'); // YYYY-MM-DD format
    console.log('Debug - Final dateIso for grouping:', dateIso);
    console.log('Debug - Appointment mapping result:', {
      id: srv.id || srv.Id,
      time: timeStr,
      title,
      dateIso,
      originalScheduledFor: scheduledForStr
    });
    return { id: srv.id || srv.Id, time: timeStr, title, status: String(status), color, column: 1, duration, dateIso };
  };

  // Assign columns to overlapping appointments (simple greedy algorithm)
  const assignColumns = (list) => {
    // compute numeric start/end minutes
    const toMinutes = (timeStr) => {
      const [hh, mm] = timeStr.split(':').map(Number);
      return hh * 60 + mm;
    };
    const items = list.map(a => ({ ...a, _start: toMinutes(a.time), _end: toMinutes(a.time) + (a.duration || 30) }));
    // sort by start, then by longer duration
    items.sort((x, y) => x._start - y._start || y._end - x._end);
    const columns = [];
    items.forEach(item => {
      let placed = false;
      for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci];
        // check against last item in column
        const last = col[col.length - 1];
        if (last._end <= item._start) {
          col.push(item);
          item._col = ci;
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([item]);
        item._col = columns.length - 1;
      }
    });
    const totalCols = Math.max(1, columns.length);
    // map back into original objects shape with column and totalColumns
    return items.map(it => ({ ...it, column: (it._col || 0) + 1, totalColumns: totalCols }));
  };

  // Load appointments when selectedDate / viewMode / currentWeek changes
  useEffect(() => {
    const loadAppointments = async () => {
      try {
        setIsLoadingAppointments(true);
        setAppointmentsError(null);
        let data = [];
        if (viewMode === 'Day') {
          // convert selectedDate (en-GB) to yyyy-mm-dd
          const d = parseDateString(selectedDate);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0'); // Tháng bắt đầu từ 0
          const day = String(d.getDate()).padStart(2, '0');
          const iso = `${year}-${month}-${day}`;
          console.log('Debug - Calendar selectedDate:', selectedDate);
          console.log('Debug - Parsed date object:', d);
          console.log('Debug - ISO date for API call:', iso);
          data = await (await import('../../services/appointmentService')).appointmentService.getAppointmentsByDate(iso, selectedExpertId);
        } else {
          // week range using weekDays fullIso
          const startIso = weekDays[0].fullIso;
          const endIso = weekDays[weekDays.length - 1].fullIso;
          data = await (await import('../../services/appointmentService')).appointmentService.getAppointmentsByDateRange(startIso, endIso, selectedExpertId);
        }

        // normalize server response
        const rawArr = Array.isArray(data) ? data : (data?.data || []);
        // client-side filter by selected expert if server didn't already
        const filteredArr = (!selectedExpertId) ? rawArr : rawArr.filter(srv => {
          const sid = String(selectedExpertId);
          if (!srv) return false;
          if (srv.therapistId && String(srv.therapistId) === sid) return true;
          if (srv.therapist && (String(srv.therapist.id) === sid || String(srv.therapist.Id) === sid)) return true;
          if (srv.therapistIdGuid && String(srv.therapistIdGuid) === sid) return true;
          if (srv.therapistGuid && String(srv.therapistGuid) === sid) return true;
          if (srv.userSession && srv.userSession.therapistId && String(srv.userSession.therapistId) === sid) return true;
          // some apis use therapistId on nested objects
          if (srv.order && srv.order.therapistId && String(srv.order.therapistId) === sid) return true;
          return false;
        });
        const mapped = filteredArr.map(mapServerToUI);
        console.log('Debug - Raw appointments from server:', rawArr);
        console.log('Debug - Filtered appointments:', filteredArr);
        console.log('Debug - Mapped appointments:', mapped);
        // For week view, assign columns per day so overlapping detection is per column/day
        if (viewMode === 'Week') {
          const byDate = {};
          mapped.forEach(m => {
            (byDate[m.dateIso] = byDate[m.dateIso] || []).push(m);
          });
          const combined = [];
          Object.keys(byDate).forEach(dateKey => {
            const group = byDate[dateKey];
            const withCols = assignColumns(group);
            combined.push(...withCols);
          });
          setAppointmentsData(combined);
        } else {
          const withCols = assignColumns(mapped);
          setAppointmentsData(withCols);
        }
      } catch (err) {
        console.error('Failed to load appointments', err);
        setAppointmentsError(err?.response?.data || err.message || String(err));
      } finally {
        setIsLoadingAppointments(false);
      }
    };

    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewMode, currentWeek, selectedExpertId, reloadToken /* weekDays is regenerated each render */]);

  return (
    <div className="appointment-calendar page-with-header">
      <Header />
      
      <div className="appointment-layout">
        {/* Left Sidebar */}
        <div className="appointment-sidebar">
          <div className="sidebar-header">
            <h3>Điều hướng</h3>
          </div>
          <div className="sidebar-menu">
            {!isEditMode && (
              <div className={`menu-item ${activeSection === 'user-details' ? 'main-active' : ''}`} onClick={() => handleNavigation('user-details')}>
                <span className="menu-icon">👤</span>
                <span>Chi tiết người dùng</span>
              </div>
            )}
            <div className={`menu-item ${activeSection === 'profile' ? 'main-active' : ''}`} onClick={() => handleNavigation('profile')}>
              <span className="menu-icon">👨‍⚕️</span>
              <span>Xem hồ sơ</span>
            </div>
            <div className={`menu-item ${activeSection === 'psychological-profile' ? 'main-active' : ''}`} onClick={() => handleNavigation('psychological-profile')}>
              <span className="menu-icon">🔍</span>
              <span>Hồ sơ tâm lý</span>
            </div>
            <div className={`menu-item ${activeSection === 'mental-health' ? 'main-active' : ''}`} onClick={() => handleNavigation('mental-health')}>
              <span className="menu-icon">🧠</span>
              <span>Sức khỏe tinh thần</span>
            </div>
            <div className={`menu-item ${activeSection === 'therapy-goals' ? 'main-active' : ''}`} onClick={() => handleNavigation('therapy-goals')}>
              <span className="menu-icon">✏️</span>
              <span>Mục tiêu trị liệu</span>
            </div>
            <div className={`menu-item ${activeSection === 'appointment' ? 'main-active' : ''}`} onClick={() => handleNavigation('appointment')}>
              <span className="menu-icon">📅</span>
              <span>Cuộc hẹn</span>
            </div>
            <div className={`menu-item ${activeSection === 'session-notes' ? 'main-active' : ''}`} onClick={() => handleNavigation('session-notes')}>
              <span className="menu-icon">💬</span>
              <span>Ghi chú & Tương tác</span>
            </div>
          </div>
          <div className="sidebar-footer">
            <button className="plus-btn" onClick={handlePlusClick}>+ Thêm</button>
          </div>
        </div>

        {/* Mobile Menu Toggle Button */}
        <div className={`mobile-menu-toggle ${isMobileMenuOpen ? 'hidden' : ''}`} onClick={handleMobileMenuToggle}>
          <span className="hamburger-icon"></span>
        </div>

        {/* Mobile Navigation Overlay */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={handleMobileMenuToggle}>
          <div className="mobile-nav-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-nav-header">
              <h3>Navigation</h3>
              <button className="close-btn" onClick={() => setIsMobileMenuOpen(false)}>×</button>
            </div>
            <div className="mobile-nav-menu">
              {!isEditMode && (
                <div className={`mobile-menu-item ${activeSection === 'user-details' ? 'active' : ''}`} onClick={() => { handleNavigation('user-details'); setIsMobileMenuOpen(false); }}>
                  <span className="menu-icon">👤</span>
                  <span>User Details</span>
                </div>
              )}
              <div className={`mobile-menu-item ${activeSection === 'profile' ? 'active' : ''}`} onClick={() => { handleNavigation('profile'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">👨‍⚕️</span>
                <span>Xem hồ sơ</span>
              </div>
              <div className={`mobile-menu-item ${activeSection === 'psychological-profile' ? 'active' : ''}`} onClick={() => { handleNavigation('psychological-profile'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">🔍</span>
                <span>Psychological Profile</span>
              </div>
              <div className={`mobile-menu-item ${activeSection === 'mental-health' ? 'active' : ''}`} onClick={() => { handleNavigation('mental-health'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">🧠</span>
                <span>Mental Health</span>
              </div>
              <div className={`mobile-menu-item ${activeSection === 'therapy-goals' ? 'active' : ''}`} onClick={() => { handleNavigation('therapy-goals'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">✏️</span>
                <span>Mục tiêu trị liệu</span>
              </div>
              <div className={`mobile-menu-item ${activeSection === 'appointment' ? 'active' : ''}`} onClick={() => { handleNavigation('appointment'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">📅</span>
                <span>Appointment</span>
              </div>
              <div className={`mobile-menu-item ${activeSection === 'session-notes' ? 'active' : ''}`} onClick={() => { handleNavigation('session-notes'); setIsMobileMenuOpen(false); }}>
                <span className="menu-icon">💬</span>
                <span>Ghi chú & Tương tác</span>
              </div>
            </div>
            <div className="mobile-nav-footer">
              <button className="mobile-plus-btn" onClick={() => { handlePlusClick(); setIsMobileMenuOpen(false); }}>+ Thêm</button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="appointment-main-content">
          {activeSection === 'profile' ? (
            /* View Profile */
            <div className="view-profile-section">
              <div className="view-profile-header">
                <h2>Xem hồ sơ</h2>
                <p>Xem toàn diện thông tin hồ sơ và cài đặt của bạn</p>
              </div>
              
              <div className="view-profile-content">
                {/* Profile Overview */}
                <div className="profile-overview">
                  <div className="profile-card">
                    <div className="profile-avatar">
                      <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjMGVhNWU5Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI0OCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlU8L3RleHQ+Cjwvc3ZnPgo=" alt="User Avatar" />
                    </div>
                    <div className="profile-info">
                      <h3>{userFormData.fullName || currentUser?.name || currentUser?.username || 'Người dùng'}</h3>
                      <p>{userFormData.email || currentUser?.email || 'Chưa có email'}</p>
                      <div className="profile-status">
                        <span className="status-indicator active"></span>
                        <span>Hoạt động</span>
                      </div>
                    </div>
                    <button className="edit-profile-btn" onClick={handleEditProfile}>Chỉnh sửa hồ sơ</button>
                  </div>
                </div>

                {/* Profile Details */}
                <div className="profile-details">
                  <div className="details-section">
                    <h4>Thông tin cá nhân</h4>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">Họ và tên</span>
                        <span className="detail-value">Nguyen Van A</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Giới tính</span>
                        <span className="detail-value">Nam</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Quốc gia</span>
                        <span className="detail-value">Vietnam</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Ngôn ngữ</span>
                        <span className="detail-value">Tiếng Việt</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4>Thông tin liên hệ</h4>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">Email</span>
                        <span className="detail-value">{userFormData.email || currentUser?.email || 'Chưa có email'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Số điện thoại</span>
                        <span className="detail-value">+84 123 456 789</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Lần đăng nhập cuối</span>
                        <span className="detail-value">2 giờ trước</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4>Cài đặt tài khoản</h4>
                    <div className="settings-list">
                      <div className="setting-item">
                        <span className="setting-label">Thông báo email</span>
                        <div className="setting-toggle">
                          <input type="checkbox" defaultChecked />
                          <span className="toggle-slider"></span>
                        </div>
                      </div>
                      <div className="setting-item">
                        <span className="setting-label">Thông báo SMS</span>
                        <div className="setting-toggle">
                          <input type="checkbox" />
                          <span className="toggle-slider"></span>
                        </div>
                      </div>
                      <div className="setting-item">
                        <span className="setting-label">Privacy Mode</span>
                        <div className="setting-toggle">
                          <input type="checkbox" defaultChecked />
                          <span className="toggle-slider"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="profile-actions">
                  <button className="download-profile-btn" onClick={handleDownloadProfile}>Tải xuống hồ sơ</button>
                  <button className="share-profile-btn" onClick={handleShareProfile}>Chia sẻ hồ sơ</button>
                  <button className="edit-profile-btn" onClick={handleEditProfile}>Chỉnh sửa hồ sơ</button>
                </div>
              </div>
            </div>
          ) : activeSection === 'user-details' ? (
            /* User Details Form */
            <div className="user-details-section">
              <div className="user-details-header">
                <h2>Chi tiết người dùng</h2>
              </div>
              
              <div className="user-profile-card">
                <div className="user-profile-header">
                  <div className="user-avatar">
                    <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjMGVhNWU5Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI0OCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlU8L3RleHQ+Cjwvc3ZnPgo=" alt="User Avatar" />
                  </div>
                  <div className="user-info">
                    <h3>{currentUser?.name || currentUser?.username || 'Người dùng'}</h3>
                    <p>{currentUser?.email || 'Chưa có email'}</p>
                  </div>
                  <button className="edit-btn" onClick={handleEditProfile}>Chỉnh sửa</button>
                </div>
                
                <div className="user-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Họ và tên</label>
                      <input 
                        type="text" 
                        placeholder="Nhập họ và tên" 
                        value={userFormData.fullName}
                        onChange={(e) => handleFormChange('fullName', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tên gọi</label>
                      <input 
                        type="text" 
                        placeholder="Nhập tên gọi" 
                        value={userFormData.nickName}
                        onChange={(e) => handleFormChange('nickName', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input 
                        type="email" 
                        placeholder="Nhập địa chỉ email" 
                        value={userFormData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Số điện thoại</label>
                      <input 
                        type="tel" 
                        placeholder="Nhập số điện thoại" 
                        value={userFormData.phone}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Giới tính</label>
                      <select 
                        value={userFormData.gender}
                        onChange={(e) => handleFormChange('gender', e.target.value)}
                      >
                        <option value="">Chọn giới tính</option>
                        <option value="male">Nam</option>
                        <option value="female">Nữ</option>
                        <option value="other">Khác</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Quốc gia</label>
                      <input 
                        type="text" 
                        placeholder="Nhập quốc gia" 
                        value={userFormData.country}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ngôn ngữ</label>
                      <select 
                        value={userFormData.language}
                        onChange={(e) => handleFormChange('language', e.target.value)}
                      >
                        <option value="">Chọn ngôn ngữ</option>
                        <option value="en">Tiếng Anh</option>
                        <option value="vi">Tiếng Việt</option>
                        <option value="zh">Tiếng Trung</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Số điện thoại</label>
                      <input 
                        type="tel" 
                        placeholder="Nhập số điện thoại" 
                        value={userFormData.phone}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="email-section">
                    <h4>Địa chỉ email của tôi</h4>
                    <div className="email-display">
                      <span className="email-icon">📧</span>
                      <span>{userFormData.email || currentUser?.email || 'Chưa có email'}</span>
                    </div>
                    <p className="email-date">1 tháng trước</p>
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="form-actions">
                    <button className="cancel-btn" onClick={handleCancelEdit}>
                      Hủy
                    </button>
                    <button className="save-btn" onClick={handleSaveUserDetails}>
                      Lưu thay đổi
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : activeSection === 'psychological-profile' ? (
            /* Psychological Profile */
            <div className="psychological-profile-section">
              <div className="psychological-profile-header">
                <h2>Psychological Profile</h2>
                <p>Comprehensive assessment of your psychological characteristics and traits</p>
              </div>
              
              <div className="psychological-profile-content">
                {/* Assessment Overview */}
                <div className="assessment-overview">
                  <div className="overview-card">
                    <div className="overview-icon">🧠</div>
                    <h3>Personality Assessment</h3>
                    <p>Understanding your core personality traits and behavioral patterns</p>
                    <div className="assessment-status">
                      <span className="status-badge completed">Completed</span>
                    </div>
                  </div>
                  
                  <div className="overview-card">
                    <div className="overview-icon">💭</div>
                    <h3>Cognitive Assessment</h3>
                    <p>Evaluating your thinking patterns and cognitive abilities</p>
                    <div className="assessment-status">
                      <span className="status-badge in-progress">In Progress</span>
                    </div>
                  </div>
                  
                  <div className="overview-card">
                    <div className="overview-icon">❤️</div>
                    <h3>Emotional Assessment</h3>
                    <p>Analyzing your emotional intelligence and regulation</p>
                    <div className="assessment-status">
                      <span className="status-badge pending">Pending</span>
                    </div>
                  </div>
                </div>

                {/* Profile Results */}
                <div className="profile-results">
                  <h3>Your Psychological Profile</h3>
                  <div className="results-grid">
                    <div className="result-card">
                      <h4>Big Five Personality</h4>
                      <div className="trait-bars">
                        <div className="trait-item">
                          <span className="trait-name">Openness</span>
                          <div className="trait-bar">
                            <div className="trait-fill" style={{width: '75%'}}></div>
                          </div>
                          <span className="trait-score">75%</span>
                        </div>
                        <div className="trait-item">
                          <span className="trait-name">Conscientiousness</span>
                          <div className="trait-bar">
                            <div className="trait-fill" style={{width: '68%'}}></div>
                          </div>
                          <span className="trait-score">68%</span>
                        </div>
                        <div className="trait-item">
                          <span className="trait-name">Extraversion</span>
                          <div className="trait-bar">
                            <div className="trait-fill" style={{width: '52%'}}></div>
                          </div>
                          <span className="trait-score">52%</span>
                        </div>
                        <div className="trait-item">
                          <span className="trait-name">Agreeableness</span>
                          <div className="trait-bar">
                            <div className="trait-fill" style={{width: '81%'}}></div>
                          </div>
                          <span className="trait-score">81%</span>
                        </div>
                        <div className="trait-item">
                          <span className="trait-name">Neuroticism</span>
                          <div className="trait-bar">
                            <div className="trait-fill" style={{width: '45%'}}></div>
                          </div>
                          <span className="trait-score">45%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="result-card">
                      <h4>Strengths & Areas for Growth</h4>
                      <div className="strengths-list">
                        <div className="strength-item">
                          <span className="strength-icon">✨</span>
                          <span>High emotional intelligence</span>
                        </div>
                        <div className="strength-item">
                          <span className="strength-icon">🎯</span>
                          <span>Strong goal orientation</span>
                        </div>
                        <div className="strength-item">
                          <span className="strength-icon">🤝</span>
                          <span>Excellent communication skills</span>
                        </div>
                      </div>
                      <div className="growth-areas">
                        <h5>Areas for Development:</h5>
                        <div className="growth-item">
                          <span className="growth-icon">📈</span>
                          <span>Stress management techniques</span>
                        </div>
                        <div className="growth-item">
                          <span className="growth-icon">🧘</span>
                          <span>Mindfulness practices</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="profile-actions">
                  <button className="retake-assessment-btn" onClick={handleRetakeAssessment}>Làm lại đánh giá</button>
                  <button className="download-profile-btn" onClick={handleDownloadProfile}>Tải xuống hồ sơ</button>
                  <button className="share-profile-btn" onClick={handleShareProfile}>Chia sẻ với nhà trị liệu</button>
                </div>
              </div>
            </div>
          ) : activeSection === 'mental-health' ? (
            /* Mental Health Tracking */
            <div className="mental-health-section">
              <div className="mental-health-header">
                <h2>Theo dõi sức khỏe tinh thần</h2>
                <p>Tiến trình tâm lý và xu hướng triệu chứng của khách hàng theo thời gian</p>
              </div>
              
              <div className="mental-health-content">
                {/* Key Metrics */}
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-header">
                      <h3>Lo âu</h3>
                      <div className="metric-trend">
                        <span className="trend-arrow">↗</span>
                        <span className="trend-value">10.78%</span>
                      </div>
                    </div>
                    <div className="metric-value">62%</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-header">
                      <h3>Trầm cảm</h3>
                      <div className="metric-trend">
                        <span className="trend-arrow">↗</span>
                        <span className="trend-value">1.08%</span>
                      </div>
                    </div>
                    <div className="metric-value">12%</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-header">
                      <h3>Chất lượng giấc ngủ</h3>
                      <div className="metric-trend">
                        <span className="trend-arrow">↗</span>
                        <span className="trend-value">5.90%</span>
                      </div>
                    </div>
                    <div className="metric-value">30%</div>
                  </div>
                </div>

                {/* Chart Tools */}
                <div className="chart-tools">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input type="text" placeholder="Tìm kiếm" />
                  </div>
                  <select className="time-filter">
                    <option value="monthly">Hàng tháng</option>
                    <option value="weekly">Hàng tuần</option>
                    <option value="daily">Hàng ngày</option>
                  </select>
                  <button className="filter-btn">Lọc</button>
                </div>

                {/* Chart Placeholder */}
                <div className="chart-container">
                  <div className="chart-header">
                    <h3>Mental Health Trends</h3>
                  </div>
                  <div className="chart-content">
                    <div className="chart-placeholder">
                      <p>📊 Stacked Bar Chart</p>
                      <p>Anxiety, Depression, Sleep Quality over time</p>
                    </div>
                  </div>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-color depression"></span>
                      <span>Depression</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color sleep"></span>
                      <span>Sleep Quality</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color anxiety"></span>
                      <span>Anxiety</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeSection === 'therapy-goals' ? (
            /* Therapy Goals */
            <div className="therapy-goals-section">
              <div className="therapy-goals-header">
                <h2>Mục tiêu trị liệu</h2>
                <p>Thiết lập và theo dõi các mục tiêu và cột mốc trị liệu cá nhân của bạn</p>
              </div>
              
              <div className="therapy-goals-content">
                {/* Goals Overview */}
                <div className="goals-overview">
                  <div className="goals-stats">
                    <div className="stat-card">
                      <div className="stat-icon">🎯</div>
                      <div className="stat-info">
                        <h3>12</h3>
                        <p>Tổng mục tiêu</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon">✅</div>
                      <div className="stat-info">
                        <h3>8</h3>
                        <p>Đã hoàn thành</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon">🔄</div>
                      <div className="stat-info">
                        <h3>4</h3>
                        <p>Đang thực hiện</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Goals */}
                <div className="current-goals">
                  <h3>Mục tiêu hiện tại</h3>
                  <div className="goals-list">
                    <div className="goal-item">
                      <div className="goal-header">
                        <h4>Giảm mức độ lo âu</h4>
                        <span className="goal-status in-progress">Đang thực hiện</span>
                      </div>
                      <p>Thực hành thiền chánh niệm 10 phút mỗi ngày</p>
                      <div className="goal-progress">
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: '65%'}}></div>
                        </div>
                        <span className="progress-text">65% Hoàn thành</span>
                      </div>
                    </div>
                    
                    <div className="goal-item">
                      <div className="goal-header">
                        <h4>Cải thiện chất lượng giấc ngủ</h4>
                        <span className="goal-status in-progress">Đang thực hiện</span>
                      </div>
                      <p>Thiết lập lịch ngủ nhất quán và thói quen trước khi ngủ</p>
                      <div className="goal-progress">
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: '40%'}}></div>
                        </div>
                        <span className="progress-text">40% Hoàn thành</span>
                      </div>
                    </div>
                    
                    <div className="goal-item">
                      <div className="goal-header">
                        <h4>Xây dựng sự tự tin</h4>
                        <span className="goal-status completed">Đã hoàn thành</span>
                      </div>
                      <p>Thực hành tự nói chuyện tích cực và khẳng định hàng ngày</p>
                      <div className="goal-progress">
                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: '100%'}}></div>
                        </div>
                        <span className="progress-text">100% Hoàn thành</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Goal Categories */}
                <div className="goal-categories">
                  <h3>Goal Categories</h3>
                  <div className="categories-grid">
                    <div className="category-card">
                      <div className="category-icon">🧠</div>
                      <h4>Mental Health</h4>
                      <p>5 goals</p>
                    </div>
                    <div className="category-card">
                      <div className="category-icon">💪</div>
                      <h4>Personal Growth</h4>
                      <p>3 goals</p>
                    </div>
                    <div className="category-card">
                      <div className="category-icon">🤝</div>
                      <h4>Relationships</h4>
                      <p>2 goals</p>
                    </div>
                    <div className="category-card">
                      <div className="category-icon">🎯</div>
                      <h4>Career</h4>
                      <p>2 goals</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="goals-actions">
                  <button className="add-goal-btn">+ Add New Goal</button>
                  <button className="view-all-btn">View All Goals</button>
                  <button className="export-goals-btn">Export Goals</button>
                </div>
              </div>
            </div>
          ) : activeSection === 'session-notes' ? (
            /* Session Notes & Interactions Form */
            <div className="session-notes-section">
              <div className="session-notes-header">
                <h2>Ghi chú & Tương tác</h2>
                <p>Theo dõi tiến trình trị liệu và những hiểu biết quan trọng</p>
              </div>
              
              <div className="session-notes-content">
                {/* Feature Cards */}
                <div className="feature-cards">
                  <div className="feature-card">
                    <div className="feature-icon">🎒</div>
                    <h3>Truy cập trọn đời</h3>
                    <p>Theo dõi các mục tiêu đã hoàn thành để chúc mừng sự phát triển của bạn.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">📖</div>
                    <h3>Hướng dẫn tự trị liệu</h3>
                    <p>Các bài tập từng bước để giúp bạn cải thiện sức khỏe cảm xúc.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">📝</div>
                    <h3>Ghi chú quan trọng</h3>
                    <p>Ghi lại những hiểu biết từ hành trình chữa lành của bạn.</p>
                  </div>
                </div>

                {/* Psychometric Results */}
                <div className="psychometric-results">
                  <h3>Kết quả tâm lý học của bạn</h3>
                  <p>Đây là kết quả của bạn dựa trên đánh giá:</p>
                  
                  <div className="result-actions">
                    <button className="share-btn">Chia sẻ kết quả</button>
                    <button className="download-btn">Tải xuống báo cáo</button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Calendar Header */}
              <div className="calendar-header">
                <div className="date-navigation">
                  <button 
                    className="nav-arrow"
                    onClick={() => {
                      if (viewMode === 'Week') {
                        setCurrentWeek(prev => prev - 1);
                      } else if (viewMode === 'Day') {
                        // Navigate to previous day based on current selected date
                        const currentDate = parseDateString(selectedDate);
                        currentDate.setDate(currentDate.getDate() - 1);
                        setSelectedDate(currentDate.toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric',
                          timeZone: 'Asia/Ho_Chi_Minh'
                        }));
                      }
                    }}
                  >‹</button>
                  <div 
                    className="date-display"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  >
                    <span className="date">
                      {viewMode === 'Day' ? selectedDate : (() => {
                        const weekDays = getWeekDays();
                        const startDate = weekDays[0];
                        const endDate = weekDays[6];
                        return `${startDate.fullDate} - ${endDate.fullDate}`;
                      })()}
                    </span>
                    <span 
                      className="today"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Set to today's date
                        const today = new Date();
                        setSelectedDate(today.toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        }));
                        setCurrentWeek(0);
                        setViewMode('Day');
                      }}
                    >Today</span>
                    <span className="dropdown">▼</span>
                  </div>
                  <button 
                    className="nav-arrow"
                    onClick={() => {
                      if (viewMode === 'Week') {
                        setCurrentWeek(prev => prev + 1);
                      } else if (viewMode === 'Day') {
                        // Navigate to next day based on current selected date
                        const currentDate = parseDateString(selectedDate);
                        currentDate.setDate(currentDate.getDate() + 1);
                        setSelectedDate(currentDate.toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric',
                          timeZone: 'Asia/Ho_Chi_Minh'
                        }));
                      }
                    }}
                  >›</button>
                </div>
                
                {/* Date Picker Modal */}
                {showDatePicker && (
                  <div className="date-picker-modal">
                    <div className="date-picker-content">
                      <div className="date-picker-header">
                        <div style={{display:'flex',alignItems:'center',gap:12}}>
                          <button className="nav-arrow" onClick={() => { const d = new Date(displayMonthDate); d.setMonth(d.getMonth() - 1); setDisplayMonthDate(d); }}>&lsaquo;</button>
                          <div style={{fontWeight:700}}>{displayMonthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</div>
                          <button className="nav-arrow" onClick={() => { const d = new Date(displayMonthDate); d.setMonth(d.getMonth() + 1); setDisplayMonthDate(d); }}>&rsaquo;</button>
                        </div>
                        <button 
                          className="close-btn"
                          onClick={() => setShowDatePicker(false)}
                        >×</button>
                      </div>
                      <div className="date-picker-calendar">
                        <div className="calendar-grid">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="calendar-day-header">{day}</div>
                          ))}
                          {generateCalendarDays(displayMonthDate).map((day, index) => (
                            <div
                              key={index}
                              className={`calendar-day ${day ? 'selectable' : 'empty'}`}
                              onClick={() => {
                                if (!day) return;
                                handleDateSelect(day);
                                setDisplayMonthDate(new Date(day));
                              }}
                            >
                              {day ? day.getDate() : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="view-controls">
                  <button 
                    className={`view-btn ${viewMode === 'Day' ? 'active' : ''}`}
                    onClick={() => setViewMode('Day')}
                  >
                    Day
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'Week' ? 'active' : ''}`}
                    onClick={() => setViewMode('Week')}
                  >
                    Week
                  </button>
                </div>
                <div className="top-controls">
                  <label style={{fontWeight:600, color:'#475569'}}>Doctor:</label>
                  <select className="doctor-select" value={selectedExpertId || ''} onChange={(e) => {
                    const val = e.target.value || null;
                    setSelectedExpertId(val);
                    const found = expertsList.find(x => String(x.id) === String(val) || String(x.Id) === String(val));
                    if (found) {
                      setExpertInfo(found);
                      localStorage.setItem('selectedExpert', JSON.stringify(found));
                    } else {
                      setExpertInfo(null);
                      localStorage.removeItem('selectedExpert');
                    }
                    setReloadToken(t => t + 1);
                  }}>
                    <option value="">-- Current --</option>
                    {expertsList.map((ex) => (
                      <option key={ex.id || ex.Id} value={ex.id || ex.Id}>{ex.name || ex.fullName || ex.displayName}</option>
                    ))}
                  </select>
                </div>
                
                <div className="appointment-controls">
                  <div className="appointment-dropdown">
                    <span>Your appointment</span>
                    <span className="dropdown">▼</span>
                  </div>
                  <button 
                    className="new-appointment-btn"
                    onClick={handleNewAppointment}
                  >
                    + New appointment
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="calendar-grid">
                {viewMode === 'Day' ? (
                  // Day View
                  <>
                    <div className="calendar-headers">
                      <div className="timezone-header">PST</div>
                      <div className="expert-header">
                        <h3>{expertInfo?.name || "Chưa chọn chuyên gia"}</h3>
                        <p>{expertInfo?.specialty || "Vui lòng chọn chuyên gia"}</p>
                      </div>
                    </div>
                    
                    <div className="calendar-content">
                      {isLoadingAppointments && (
                        <div className="appointments-loading">Loading appointments...</div>
                      )}
                      {appointmentsError && (
                        <div className="appointments-error">
                          <div>Failed to load appointments: {String(appointmentsError)}</div>
                          <button className="retry-btn" onClick={() => setReloadToken(t => t + 1)}>Retry</button>
                        </div>
                      )}
                      <div className="time-column">
                        {timeSlots.map((time) => (
                          <div key={time} className="time-slot">
                            {time}
                          </div>
                        ))}
                      </div>
                      
                      <div className="schedule-grid">
                        <div className="appointments-area">
                          {/* Current time indicator */}
                          <div 
                            className="current-time-line"
                            style={{ 
                              top: `${(timeSlots.indexOf(currentTime) * 90)}px` 
                            }}
                          ></div>
                          
                          {appointments.map((appointment) => {
                            // scope same-time detection to appointments that share the same date
                            const sameTimeAppointments = appointments.filter(apt => apt.time === appointment.time && apt.dateIso === appointment.dateIso);
                            const appointmentIndex = sameTimeAppointments.findIndex(apt => apt.id === appointment.id);
                            const timeIndex = timeSlots.indexOf(appointment.time);
                            const topOffset = (timeIndex * 90) + (appointmentIndex * 5); // 80px height + 10px margin-bottom
                            
                            console.log('Debug - Appointment positioning:', {
                              id: appointment.id,
                              time: appointment.time,
                              timeIndex,
                              topOffset,
                              timeSlots: timeSlots.slice(0, 5) + '...'
                            });
                            
                            return (
                            <div 
                              key={appointment.id}
                              className={`appointment-block ${appointment.color}`}
                              style={{ 
                                top: `${topOffset}px`,
                                left: `${(appointment.column - 1) * 250 + 10}px`,
                                width: '180px',
                                height: `${Math.max((appointment.duration / 30) * 40, 60)}px`
                              }}
                              onClick={() => handleAppointmentClick(appointment)}
                            >
                              <div className="appointment-content">
                                <div className="appointment-title">{appointment.title}</div>
                                <div className="appointment-status">{appointment.status}</div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Week View
                  <>
                    <div className="calendar-headers week-headers">
                      <div className="timezone-header">PST</div>
                      {weekDays.map((day, index) => (
                        <div key={index} className="day-header">
                          <div className="day-name">{day.day}</div>
                          <div className="day-date">{day.date}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="calendar-content week-content">
                      <div className="time-column">
                        {timeSlots.map((time) => (
                          <div key={time} className="time-slot">
                            {time}
                          </div>
                        ))}
                      </div>
                      
                      <div className="week-grid">
                        {weekDays.map((day, dayIndex) => (
                          <div key={dayIndex} className="day-column">
                            <div className="appointments-area">
                              {/* Current time indicator */}
                              <div 
                                className="current-time-line"
                                style={{ 
                                  top: `${(timeSlots.indexOf(currentTime) * 40)}px` 
                                }}
                              ></div>
                              
                              {appointments.map((appointment, index) => {
                                // scope same-time detection to same date
                                const sameTimeAppointments = appointments.filter(apt => apt.time === appointment.time && apt.dateIso === appointment.dateIso);
                                const appointmentIndex = sameTimeAppointments.findIndex(apt => apt.id === appointment.id);
                                const timeIndex = timeSlots.indexOf(appointment.time);
                                const topOffset = (timeIndex * 40) + (appointmentIndex * 5);
                                
                                return (
                                <div 
                                  key={appointment.id}
                                  className={`appointment-block ${appointment.color}`}
                                  style={{ 
                                    top: `${topOffset}px`,
                                    left: '10px',
                                    width: 'calc(100% - 20px)',
                                    height: `${Math.max((appointment.duration / 30) * 40, 60)}px`
                                  }}
                                  onClick={() => handleAppointmentClick(appointment)}
                                >
                                  <div className="appointment-content">
                                    <div className="appointment-title">{appointment.title}</div>
                                    <div className="appointment-status">{appointment.status}</div>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      <Footer />
      {/* Appointment Details Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            {modalLoading ? (
              <div className="modal-loading">Loading...</div>
            ) : modalError ? (
              <div className="modal-error">Error loading appointment: {String(modalError)}</div>
            ) : modalAppointment ? (
              <div className="modal-body">
                <h3>Appointment Details</h3>
                {(() => {
                  // determine client display name
                  const clientName = modalClientName || modalAppointment.clientName || modalAppointment.client?.fullName || modalAppointment.client?.name || modalAppointment.client?.displayName || modalAppointment.client?.username || modalAppointment.clientId || modalAppointment.userSession?.user?.fullName || modalAppointment.userSession?.User?.fullName || 'Unknown';

                  // determine friendly status
                  // use nullish coalescing so numeric 0 is preserved
                  const rawStatus = modalAppointment.status ?? modalAppointment.statusValue ?? modalAppointment.StatusValue ?? modalAppointment.state ?? modalAppointment.statusText ?? 'Unknown';
                  const statusStr = (typeof rawStatus === 'number') ? (rawStatus === 0 ? 'Scheduled' : rawStatus === 1 ? 'Confirmed' : rawStatus === 2 ? 'Cancelled' : String(rawStatus)) : String(rawStatus);

                  // Debug log để kiểm tra dữ liệu
                  console.log('Modal appointment data:', modalAppointment);
                  console.log('ScheduledFor value:', modalAppointment.scheduledFor || modalAppointment.ScheduledFor || modalAppointment.scheduled_for);
                  
                  return (
                    <>
                      <div className="modal-field"><span className="label">Client</span><span className="value">{modalClientLoading ? 'Loading...' : clientName}</span></div>
                      <div className="modal-field"><span className="label">Scheduled For</span><span className="value">{(() => {
                        const scheduledForStr = modalAppointment.scheduledFor || modalAppointment.ScheduledFor || modalAppointment.scheduled_for;
                        const dt = new Date(scheduledForStr);
                        console.log('Modal display - scheduledForStr:', scheduledForStr);
                        console.log('Modal display - parsed Date:', dt);
                        console.log('Modal display - local date:', dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
                        console.log('Modal display - local time:', dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
                        return `${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, ${dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
                      })()}</span></div>
                      <div className="modal-field"><span className="label">Duration</span><span className="value">{modalAppointment.durationMinutes || modalAppointment.DurationMinutes || modalAppointment.duration || 30} minutes</span></div>
                      <div className="modal-field"><span className="label">Status</span><span className="value">{statusStr}</span></div>
                    </>
                  );
                })()}
                {modalAppointment.notes && (<div className="modal-field"><span className="label">Notes</span><span className="value">{modalAppointment.notes}</span></div>)}
                {/* Payment / Order details */}
                {modalAppointment.order && (
                  <div className="modal-section">
                    <h4>Payment</h4>
                    <div className="modal-field"><span className="label">Order ID</span><span className="value">{modalAppointment.order.id || modalAppointment.orderId}</span></div>
                    <div className="modal-field"><span className="label">Amount</span><span className="value">{modalAppointment.order.amountPaid || modalAppointment.order.amount || modalAppointment.amountPaid || ''}</span></div>
                    <div className="modal-field"><span className="label">Status</span><span className="value">{modalAppointment.order.paymentStatus || modalAppointment.order.paymentStatusValue || modalAppointment.order.paymentStatusValue || ''}</span></div>
                  </div>
                )}

                <div className="modal-actions" style={{display: 'flex', gap: 8}}>
                  <button className="modal-link-btn" onClick={() => handleGoToClientProfile(modalAppointment.therapistId || modalAppointment.therapist?.id || modalAppointment.clientId || modalAppointment.client?.id)}>View Profile</button>
                  <button className="modal-edit-btn" onClick={() => handleEditAppointment(modalAppointment)}>Edit</button>
                  <button className="modal-cancel-btn" onClick={() => handleCancelAppointment(modalAppointment)}>Cancel</button>
                  <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>Close</button>
                </div>
              </div>
            ) : (
              <div className="modal-empty">No appointment selected</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
